import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { GraduationCap, Users, CalendarDays, UserPlus, Search, Phone, Pencil, UserRound, DollarSign, Briefcase, Mail } from 'lucide-react';
import { getTeachers, getTeacher, TeacherRow, TeacherDetail } from '../api';
import { teacherPhotoUrl } from '../api';
import { getWhatsAppLink } from '../../../utils/whatsapp';
import { resumenPago } from '../components/EsquemaPago';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import TeacherForm from '../components/TeacherForm';
import { readCache, writeCache } from '../../../services/localCache';
import PersonAvatar from '../../../components/PersonAvatar';
import { useAuth } from '../../../context/AuthContext';

// Dos grupos en la misma pantalla (Mabel 3-oct-2026: «¿puedes ponerle al staff también un perfil,
// como a los maestros?»): PROFESORES (quien da clases) y STAFF (gerencia, recepción, caja, marketing,
// dueñas). El grupo va en la URL (?tab=staff) para que «Volver» desde la ficha caiga en el correcto.
type Grupo = 'profes' | 'staff';

export default function TeachersListPage() {
  const { hasModule } = useAuth();
  const [params, setParams] = useSearchParams();
  const grupo: Grupo = params.get('tab') === 'staff' ? 'staff' : 'profes';
  const esStaff = grupo === 'staff';
  const cacheKey = esStaff ? 'staff-list' : 'teachers-list';
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const [data, setData] = useState<TeacherRow[] | null>(() => readCache<TeacherRow[]>(cacheKey) ?? null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<TeacherDetail | null>(null);
  const [loadingEdit, setLoadingEdit] = useState<number | null>(null);
  const [q, setQ] = useState('');
  // «Ver inactivos» (Mabel 1-oct-2026): los dados de baja no salen por defecto; con esto se
  // consultan o se reactivan. La caché local guarda SOLO la lista normal (sin inactivos).
  const [verInactivos, setVerInactivos] = useState(false);
  function load() {
    getTeachers({ inactivos: verInactivos, equipo: esStaff })
      .then((r) => { setData(r.data); if (!verInactivos) writeCache(cacheKey, r.data); })
      .catch(() => setData((prev) => prev ?? []));
  }
  useEffect(() => { load(); }, [verInactivos, grupo]);   // eslint-disable-line react-hooks/exhaustive-deps
  const nInactivos = data ? data.filter((r) => r.active === 0).length : 0;

  function cambiarGrupo(g: Grupo) {
    if (g === grupo) return;
    setData(readCache<TeacherRow[]>(g === 'staff' ? 'staff-list' : 'teachers-list') ?? null);
    setQ('');
    setParams(g === 'staff' ? { tab: 'staff' } : {}, { replace: true });
  }

  // "Editar" desde la fila (patrón Jesara): trae la ficha completa y abre el mismo modal.
  function openEdit(id: number) {
    setLoadingEdit(id);
    getTeacher(id).then((r) => setEditing(r.data)).finally(() => setLoadingEdit(null));
  }

  const visible = useMemo(() => {
    if (!data) return null;
    const t = q.trim().toLowerCase();
    if (!t) return data;
    return data.filter((r) => `${r.firstname} ${r.lastname} ${r.puesto || ''}`.toLowerCase().includes(t));
  }, [data, q]);

  const Icono = esStaff ? Briefcase : GraduationCap;
  const singular = esStaff ? 'persona del staff' : 'profesor';
  const plural = esStaff ? 'personas del staff' : 'profesores';

  return (
    // Rediseño 28-jul (lista estilo Jesara "Doctores"): UNA tarjeta con filas a lo ancho
    // (foto + nombre + contacto | Ver perfil + Editar + carga) en vez de tarjetitas 2-col.
    <div>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
            <Icono className="text-brand-blue" size={24} /> {esStaff ? 'Staff' : 'Profesores'}
          </h1>
          <p className="mt-0.5 text-sm text-content-3">
            {esStaff ? 'Gerencia, recepción, caja y marketing: quien trabaja en el estudio sin dar clases.' : 'El equipo de instructores y su carga de clases.'}
          </p>
        </div>
        {/* El staff no se da de alta aquí: nace en Usuarios (invitación con su puesto). */}
        {!esStaff && (
          <button onClick={() => setAdding(true)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
            <UserPlus size={16} /> <span className="hidden sm:inline">Nuevo profesor</span>
          </button>
        )}
      </div>

      {adding && <TeacherForm onClose={() => setAdding(false)} onSaved={() => { setAdding(false); setData(null); load(); }} />}
      {editing && <TeacherForm teacher={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {/* Profesores | Staff */}
      <div className="mb-4 inline-flex rounded-xl border border-line bg-surface p-1 text-sm" role="tablist" data-testid="equipo-tabs">
        {([['profes', 'Profesores', GraduationCap], ['staff', 'Staff', Briefcase]] as const).map(([k, label, Ic]) => (
          <button key={k} role="tab" aria-selected={grupo === k} onClick={() => cambiarGrupo(k)} data-testid={`tab-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-medium transition ${
              grupo === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>
            <Ic size={15} /> {label}
          </button>
        ))}
      </div>

      {/* Buscador (filtro en memoria) + «Ver inactivos» */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="relative w-full max-w-md">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={esStaff ? 'Buscar por nombre o puesto…' : 'Buscar profesor…'}
          className="w-full rounded-xl border border-line bg-surface py-2 pl-9 pr-3 text-sm text-content outline-none placeholder:text-content-3 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
      </div>
      <label className="inline-flex cursor-pointer select-none items-center gap-2 text-sm text-content-2">
        <input type="checkbox" checked={verInactivos} onChange={(e) => { setVerInactivos(e.target.checked); setData(null); }}
          className="h-4 w-4 rounded border-line accent-current" data-testid="ver-inactivos" />
        Ver inactivos{verInactivos && nInactivos > 0 ? ` (${nInactivos})` : ''}
      </label>
      </div>
      {/* Mabel (1-oct) buscaba aquí a Karla, Aracely y a ella misma; desde el 3-oct están en la
          pestaña Staff. Las altas y los permisos del staff siguen viviendo en Usuarios. */}
      {esStaff && hasModule('users_admin') && <p className="-mt-2 mb-4 text-xs text-content-3">
        Para dar de alta a alguien del staff o cambiar su puesto y permisos, ve a{' '}
        <Link to="/usuarios" className="font-medium text-accent-ink hover:underline">Usuarios</Link>.
        {' '}Quien da clases, aunque también sea de recepción, aparece en Profesores.
      </p>}

      {!visible ? (
        <Skeleton className="h-64 w-full rounded-2xl" />
      ) : visible.length === 0 ? (
        <EmptyState icon={Icono} title={q ? 'Sin resultados' : esStaff ? 'Sin staff' : 'Sin profesores'}
          subtitle={q ? `Nada coincide con «${q}».` : esStaff ? 'No hay personal administrativo registrado.' : 'No hay instructores registrados.'} />
      ) : (
        <div className="overflow-hidden rounded-2xl bg-surface shadow-sm">
          <div className="flex items-center gap-2 border-b border-line px-5 py-3 text-sm font-semibold text-content">
            <Icono size={16} className="text-accent-ink" /> {visible.length} {visible.length === 1 ? singular : plural}
          </div>
          <div className="divide-y divide-line">
            {visible.map((t) => {
              const wa = getWhatsAppLink(t.phone_prefix, t.phone_number, `Hola ${t.firstname} 👋`);
              const phone = `${t.phone_prefix || ''}${t.phone_number || ''}`.trim();
              return (
                <div key={t.account_id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4">
                  {/* En móvil las acciones bajan a su propia línea (w-full) para no truncar el nombre. */}
                  <Link to={`/profesores/${t.account_id}`} className="flex w-full min-w-0 items-center gap-3 hover:opacity-90 sm:w-auto sm:flex-1">
                    {/* Con moneda de «debe» (fiados de vitrina), igual que los alumnos en su lista. */}
                    <PersonAvatar userId={t.account_id} hasPhoto={t.has_photo} photoSrc={teacherPhotoUrl(t.account_id)}
                      name={t.firstname} lastName={t.lastname} size="h-12 w-12" initialClass="text-base" className="ring-1 ring-line rounded-full" />
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className={`truncate font-display font-bold ${t.active === 0 ? 'text-content-3' : 'text-content'}`}>{t.firstname} {t.lastname}</span>
                        {esStaff && t.puesto && (
                          <span className="shrink-0 rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent-ink" data-testid="staff-puesto">{t.puesto}</span>
                        )}
                        {t.active === 0 && <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-content-3">Inactivo</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-content-3">
                        {esStaff ? (
                          <>
                            {t.email && <span className="inline-flex min-w-0 items-center gap-1"><Mail size={12} className="shrink-0" /> <span className="truncate">{t.email}</span></span>}
                            {phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> +{phone}</span>}
                            {!t.email && !phone && <span>Sin datos de contacto</span>}
                          </>
                        ) : (
                          <>
                            <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {t.classes} clases</span>
                            <span className="inline-flex items-center gap-1"><Users size={12} /> {t.students} alumnos</span>
                            {/* Cómo cobra, con la MISMA frase que la ficha y la nómina (resumenPago). */}
                            <span className="inline-flex items-center gap-1 text-content-3"><DollarSign size={12} /> {resumenPago(t)}</span>
                            {phone && <span className="hidden items-center gap-1 sm:inline-flex"><Phone size={12} /> +{phone}</span>}
                          </>
                        )}
                      </div>
                    </div>
                  </Link>
                  <div className="flex w-full shrink-0 items-center justify-end gap-2 sm:w-auto">
                    {wa && (
                      <a href={wa} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-xs font-medium text-emerald-600 transition hover:bg-surface-2 dark:text-emerald-400">
                        <Phone size={13} /> WhatsApp
                      </a>
                    )}
                    <Link to={`/profesores/${t.account_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:opacity-90">
                      <UserRound size={14} /> <span className="hidden sm:inline">Ver perfil</span>
                    </Link>
                    {/* El modal de alta/edición es de profesores (usuario de portal, etc.); el staff se edita campo a campo en su ficha. */}
                    {!esStaff && (
                      <button onClick={() => openEdit(t.account_id)} disabled={loadingEdit === t.account_id}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                        <Pencil size={14} /> <span className="hidden sm:inline">{loadingEdit === t.account_id ? 'Abriendo…' : 'Editar'}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
